import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Star, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;


const Products = ({ user }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [slideIndices, setSlideIndices] = useState({});

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize slide indices when products change
  useEffect(() => {
    const initial = {};
    products.forEach((p) => { initial[p.id] = 0; });
    setSlideIndices(initial);
  }, [products]);

  // Auto-advance all cards every 4s where multiple images exist
  useEffect(() => {
    if (!products || products.length === 0) return;
    const id = setInterval(() => {
      setSlideIndices((prev) => {
        const next = { ...prev };
        products.forEach((p) => {
          const imgs = p.images || [];
          if (imgs.length > 1) {
            const cur = next[p.id] || 0;
            next[p.id] = (cur + 1) % imgs.length;
          } else {
            next[p.id] = 0;
          }
        });
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [products]);

  const filteredProducts = products.filter((product) => {
    if (filter === 'all') return true;
    const cats = (product.categories || []).map((c) => c.toLowerCase());
    return cats.includes(filter.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-24">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text" data-testid="products-page-title">
          Featured Products
        </h1>
        <p className="text-gray-400 mb-12 text-lg">
          Explore our curated selection of outfits and accessories.
        </p>

        {/* Filter Buttons (mirrors Designers.jsx styling) */}
        <div className="flex flex-wrap gap-4 mb-12" data-testid="product-filter-section">
          {['all', 'men', 'women', 'bridal', 'traditional', 'western', 'casual', 'accessories'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${filter === f
                ? 'bg-[#d4a574] text-[#0a0a0a]'
                : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a]'
                }`}
              data-testid={`product-filter-${f}-btn`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Products Grid (mirrors designers grid/cards) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="glass-card overflow-hidden card-hover"
              data-testid={`product-card-${product.id}`}
              onClick={() => navigate(`/products/${product.id}`)}
              >
              <div className="aspect-[3/4] relative overflow-hidden">
                {(() => {
                  const images = product.images || [];
                  const idx = slideIndices[product.id] || 0;
                  const src = images[idx] || 'https://via.placeholder.com/400x500?text=Product';
                  const hasMultiple = images.length > 1;
                  return (
                    <>
                      <img
                        src={src}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 select-none"
                      />
                      {hasMultiple && (
                        <>
                          {/* Prev/Next controls */}
                          <button
                            type="button"
                            aria-label="Previous image"
                            onClick={(e) => { e.stopPropagation(); setSlideIndices((prev) => ({ ...prev, [product.id]: (idx - 1 + images.length) % images.length })); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            aria-label="Next image"
                            onClick={(e) => { e.stopPropagation(); setSlideIndices((prev) => ({ ...prev, [product.id]: (idx + 1) % images.length })); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                          {/* Dots */}
                          <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2">
                            {images.map((_, dotIdx) => (
                              <button
                                key={dotIdx}
                                aria-label={`Go to image ${dotIdx + 1}`}
                                onClick={(e) => { e.stopPropagation(); setSlideIndices((prev) => ({ ...prev, [product.id]: dotIdx })); }}
                                className={`h-2.5 w-2.5 rounded-full ${dotIdx === idx ? 'bg-white' : 'bg-white/40'}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-2xl font-bold mb-2 text-white">{product.name}</h3>
                        <div className="flex items-center gap-2 text-[#d4a574] mb-2">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="font-semibold">{Number(product.rating || 0).toFixed(1)}</span>
                          <span className="text-gray-400 text-sm">({product.total_reviews || 0} reviews)</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="p-6">
                {(product.categories && product.categories.length > 0) && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {product.categories.slice(0, 3).map((cat, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-xs flex items-center gap-1"
                      >
                        <Tag className="w-3 h-3" /> {cat}
                      </span>
                    ))}
                  </div>
                )}

                {(product.colors && product.colors.length > 0) && (
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs text-gray-400">Colours:</span>
                    {product.colors.slice(0, 5).map((c, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-xs">{c}</span>
                    ))}
                    {product.colors.length > 5 && (
                      <span className="text-xs text-gray-500">+{product.colors.length - 5}</span>
                    )}
                  </div>
                )}

                {(product.sizes && product.sizes.length > 0) && (
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="text-xs text-gray-400">Sizes:</span>
                    {product.sizes.slice(0, 6).map((s, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-xs">{s}</span>
                    ))}
                    {product.sizes.length > 6 && (
                      <span className="text-xs text-gray-500">+{product.sizes.length - 6}</span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#d4a574]">{Number(product.price || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                  <span className="text-gray-400 text-sm">incl. taxes</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-20" data-testid="no-products-message">
            <p className="text-gray-400 text-lg">No products found for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;